import { shallowRef, type Ref } from 'vue'
import noUiSlider, { type API, type Options, type target as NoUiTarget } from 'nouislider'

// noUiSlider's own range shape ({ min, max, '<pct>%': [value, step], … }).
export type NoUiRange = Options['range']

// Thin Vue 3 composable that owns the imperative noUiSlider lifecycle.
// Used by RangeSlider.vue. Extracted as a composable so the slider logic
// can be unit tested without mounting the SFC.
//
// Contract:
//   - init() must be called once the target element ref is populated
//     (typically inside onMounted of the consumer component).
//   - setValue(n) drives the slider from the outside (e.g. from a manual
//     input). It is suppressed for the value that the slider just emitted
//     so the parent's reactive write-back does not feed an `update` event
//     straight back into slider.set() (infinite loop / fight between
//     handle + manual input).
//   - destroy() must be called from onBeforeUnmount.

export interface UseNoUiSliderOptions {
  start: number
  min: number
  max: number
  step: number
  // Full noUiSlider range (e.g. { min: [1, 1], '50%': [10, 5], max: [50] }) for non-linear
  // per-segment stepping; when given it wins over the flat min/max pair.
  range?: NoUiRange
  formatTooltip?: ((value: number) => string) | null
  onChange?: (value: number) => void
}

export function useNoUiSlider(
  elRef: Ref<HTMLElement | null>,
  options: UseNoUiSliderOptions,
) {
  // shallowRef so the noUiSlider instance is not wrapped in a reactive
  // proxy — handles need to be passed back to the library by reference.
  const instance = shallowRef<API | null>(null)
  // Last value the slider itself emitted; setValue() short-circuits when the
  // incoming write equals this value.
  let lastEmittedValue: number | null = null

  function buildTooltipFormatter() {
    const userFormat = options.formatTooltip
    return {
      to: (value: number) => (userFormat ? userFormat(value) : String(value)),
      from: Number,
    }
  }

  function init(): void {
    const target = elRef.value
    if (!target) return

    const slider = noUiSlider.create(target, {
      start: options.start,
      connect: 'lower',
      range: options.range ?? { min: options.min, max: options.max },
      step: options.step,
      tooltips: [buildTooltipFormatter()],
    })

    slider.on('update', (values) => {
      const raw = values[0]
      if (raw === undefined) return
      const numeric = parseFloat(String(raw))
      if (Number.isNaN(numeric)) return
      lastEmittedValue = numeric
      if (options.onChange) options.onChange(numeric)
    })

    instance.value = slider
  }

  function setValue(value: number | string): void {
    if (!instance.value) return
    // Feedback-loop guard: ignore writes that simply echo what the slider
    // just emitted. Compare on a parsed Number to avoid 4 vs "4.00" mismatch.
    const numeric = Number(value)
    if (lastEmittedValue !== null && numeric === lastEmittedValue) return
    instance.value.set(numeric)
  }

  function updateRange({ min, max }: { min: number; max: number }): void {
    if (!instance.value) return
    instance.value.updateOptions({ range: { min, max } }, false)
  }

  function destroy(): void {
    // After noUiSlider.create(target, …) the DOM element gains a `.noUiSlider`
    // back-reference — typed as `target` (TargetElement) by the library.
    const slot = elRef.value as NoUiTarget | null
    if (slot?.noUiSlider) slot.noUiSlider.destroy()
    instance.value = null
    lastEmittedValue = null
  }

  return { instance, init, setValue, updateRange, destroy }
}
